import { AiSuitePage } from '@euler/app/flows/ai-suite/AiSuitePage';
import { useCurrentUser } from '@euler/app/flows/auth/context';
/* eslint-disable @typescript-eslint/no-use-before-define */
import { useAppNavigation } from '@euler/app/components/AppNavigationProvider';
import { PlatformNotEnabledScreen } from '@euler/app/components/PlatformNotEnabledScreen';
import { AppSidebar } from '@euler/app/components/sidebar/AppSidebar';
import { useSidebarState } from '@euler/app/components/sidebar/useSidebarCollapsed';
import { AiEditionTabBar } from '@euler/app/flows/ai-edition/AiEditionTabBar';
import { kAiEditionTabScreens } from '@euler/app/flows/ai-edition/AiEditionTabScreens';
import { kAiEditionTabs } from '@euler/app/flows/ai-edition/aiEditionTabs';
import { TabBar } from '@euler/app/flows/home/components/TabBar';
import { TabContextProvider } from '@euler/app/flows/home/functions/TabContext';
import { DashboardScreen } from '@euler/app/flows/home/pages/Dashboard';
import { HistoryScreen } from '@euler/app/flows/home/pages/History';
import { InboxFlow } from '@euler/app/flows/inbox';
import { LeadsFlow } from '@euler/app/flows/leads';
import { MeFlow } from '@euler/app/flows/me';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { MaintenancePlanFlow } from '@euler/app/flows/plans';
import { ReservationsFlow } from '@euler/app/flows/reservations';
import { withStoreFeature } from '@euler/components/entitlements/withStoreFeature';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { clientPlatformRefused$ } from '@euler/functions/entitlements/clientPlatformRefused';
import { storeFeatureOfClientPlatform } from '@euler/functions/entitlements/storeFeatureOfClientPlatform';
import { useStoreFeatureLookup } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import {
  useAppLaunchConfig,
  useStoreConfiguration,
} from '@euler/functions/useStoreConfiguration';
import { useCurrentUserRole } from '@euler/functions/user/useCurrentUserRole';
import { useRefreshProfileOnForeground } from '@euler/functions/user/useRefreshProfileOnForeground';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { StoreFeature } from '@euler/model/enum';
import { useBehaviorSubject } from '@euler/utils/hooks';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import {
  DrawerContentComponentProps,
  createDrawerNavigator,
  useDrawerProgress,
} from '@react-navigation/drawer';
import { LinearGradient } from 'expo-linear-gradient';
import { FC, ReactNode, memo, useEffect, useMemo } from 'react';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';
import Animated, {
  interpolate,
  useAnimatedStyle,
} from 'react-native-reanimated';

const TabNav = createBottomTabNavigator();
const Drawer = createDrawerNavigator();

const AllTabScreens = {
  AiHome: withStoreFeature(AiSuitePage, StoreFeature.AiAssistant),
  Dashboard: DashboardScreen,
  History: HistoryScreen,
  Plans: withStoreFeature(MaintenancePlanFlow, StoreFeature.MaintenancePlan),
  Leads: withStoreFeature(LeadsFlow, StoreFeature.Lead),
  Reservations: withStoreFeature(ReservationsFlow, StoreFeature.Reservation),
  Me: MeFlow,
  // Its in-app notifications are every store's; chat inside it is gated.
  Inbox: InboxFlow,
};

export type MainTabsParams = ScreenToParams<
  typeof AllTabScreens & typeof kAiEditionTabScreens
>;

const useTabScreens = (): typeof AllTabScreens => {
  const storeConfiguration = useStoreConfiguration();
  const role = useCurrentUserRole();
  const helper = new OrgUserRoleHelper(role);
  const canAccessRecords =
    !storeConfiguration.restrictedRolesForServiceRecords?.length ||
    helper.matchesAny(storeConfiguration.restrictedRolesForServiceRecords);
  if (canAccessRecords) return AllTabScreens;
  // Hide History tab when the current role is restricted.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { History: _History, ...rest } = AllTabScreens;
  return rest as typeof AllTabScreens;
};

const MainTabs = memo(() => {
  const styles = useStyles();
  const workshopScreens = useTabScreens();
  const currentUser = useCurrentUser();
  const aiEdition = currentUser?.store.editionProductLine === 'ai';
  const featureOf = useStoreFeatureLookup();
  // The AI edition's own tabs come first; the workshop screens stay
  // registered so links into them keep working.
  const tabScreens = useMemo(
    () =>
      aiEdition
        ? { ...kAiEditionTabScreens, ...workshopScreens }
        : workshopScreens,
    [aiEdition, workshopScreens],
  );
  return (
    <TabContextProvider>
      <TabNav.Navigator
        key={`${currentUser?.user.userId}:${currentUser?.store.id}`}
        initialRouteName={
          aiEdition
            ? kAiEditionTabs.find(
                x => x.feature == null || featureOf(x.feature).enabled,
              )?.name ?? 'AiHome'
            : 'Dashboard'
        }
        screenOptions={{
          tabBarShowLabel: false,
          tabBarLabelStyle: {
            height: 0,
            display: 'none',
          },
          tabBarStyle: {
            backgroundColor: '#ffffff',
            borderTopWidth: 0,
            borderTopColor: 'transparent',
            ...styles.shadow,
          },
        }}
        tabBar={props =>
          aiEdition ? <AiEditionTabBar {...props} /> : <TabBar {...props} />
        }
      >
        {spreadScreens(TabNav.Screen, tabScreens)}
      </TabNav.Navigator>
    </TabContextProvider>
  );
});

const SidebarContent = memo((props: DrawerContentComponentProps) => {
  return <AppSidebar {...props} />;
});

// Mirrors the pre-refactor drawer animation: as the drawer opens, the
// content pane scales down, dims, and rounds its corners.
const DrawerSceneWrapper: FC<{ children: ReactNode }> = memo(({ children }) => {
  const progress =
    useDrawerProgress() as unknown as Animated.SharedValue<number>;
  const maxRadius = 16;
  const style = useAnimatedStyle(() => ({
    borderRadius: interpolate(progress.value, [0, 1], [0, maxRadius]),
    opacity: interpolate(progress.value, [0, 1], [1, 0.75]),
    transform: [{ scale: interpolate(progress.value, [0, 1], [1, 0.8]) }],
  }));
  return (
    <Animated.View
      style={[
        {
          flex: 1,
          overflow: 'hidden',
          backgroundColor: '#fff',
        },
        style,
      ]}
    >
      {children}
    </Animated.View>
  );
});

const DrawerHostedTabs = memo(() => (
  <DrawerSceneWrapper>
    <MainTabs />
  </DrawerSceneWrapper>
));

export const MainScreen = wrapNavigatorScreen(
  () => {
    const isDesktop = useIsDesktopLayout();
    const navigation = useAppNavigation();
    const { event$ } = useOrderContext();

    useAppLaunchConfig();
    useRefreshProfileOnForeground();
    const featureOf = useStoreFeatureLookup();
    const platformFeature = storeFeatureOfClientPlatform();
    const [platformRefused] = useBehaviorSubject(clientPlatformRefused$);

    useEffect(() => {
      const sub = event$.subscribe(e => {
        if (e.isMainOrder && (e.type === 'created' || e.type === 'rollback')) {
          navigation.navigate('Dashboard' as never);
        }
      });
      return () => sub.unsubscribe();
    }, [navigation, event$]);

    // The Drawer.Navigator stays in the tree on every layout so the
    // navigation graph (Main > Tabs > <tab>) matches the linking config.
    // On desktop we hide the drawer chrome and render the sidebar as a
    // sibling flex row: `drawerType: 'permanent'` lays out as absolute
    // overlay on react-native-web and leaves content hidden underneath.
    const drawer = (
      <Drawer.Navigator
        drawerContent={props =>
          isDesktop ? null : <SidebarContent {...props} />
        }
        screenOptions={{
          drawerType: isDesktop ? 'front' : 'slide',
          drawerHideStatusBarOnOpen: false,
          overlayColor: isDesktop ? 'transparent' : 'transparent',
          drawerStyle: {
            width: isDesktop ? 0 : '57%',
            backgroundColor: 'transparent',
            borderRightWidth: 0,
          },
          sceneContainerStyle: { backgroundColor: 'transparent' },
          headerShown: false,
          swipeEnabled: !isDesktop,
        }}
      >
        <Drawer.Screen
          name="Tabs"
          component={isDesktop ? MainTabs : DrawerHostedTabs}
        />
      </Drawer.Navigator>
    );

    // The server refuses every request of a build the store lacks.
    if (
      platformFeature != null &&
      (platformRefused || !featureOf(platformFeature).enabled)
    ) {
      return <PlatformNotEnabledScreen feature={platformFeature} />;
    }

    if (isDesktop) {
      return <DesktopShell drawer={drawer} />;
    }

    // Mobile: the slide drawer reveals the gradient behind it as the
    // content pane scales/dims, matching the pre-refactor look.
    return (
      <LinearGradient colors={['#009FD4', '#2669db']} style={{ flex: 1 }}>
        {drawer}
      </LinearGradient>
    );
  },
  {
    title: 'Main',
    headerShown: false,
  },
);

const kSidebarMaxExpandedWidth = 280;
const kSidebarMinExpandedWidth = 220;
const kSidebarCollapsedWidth = 64;
const kSidebarFloatingWidth = 300;
const kSidebarScaleStartsBelow = 1200;

const useSidebarExpandedWidth = () => {
  const { width: vw } = useWindowDimensions();
  if (vw >= kSidebarScaleStartsBelow) return kSidebarMaxExpandedWidth;
  // Scale proportionally below 1200, but never narrower than the
  // minimum readable width.
  const scaled = (vw * kSidebarMaxExpandedWidth) / kSidebarScaleStartsBelow;
  return Math.max(kSidebarMinExpandedWidth, Math.round(scaled));
};

const DesktopShell: FC<{ drawer: JSX.Element }> = memo(({ drawer }) => {
  const { state, cycle, closeFloating } = useSidebarState();
  const expandedWidth = useSidebarExpandedWidth();

  // Pinned column width — what occupies space in the row. Floating
  // leaves the rail in place so content doesn't shift; expanded pins
  // the full panel.
  const pinnedWidth =
    state === 'expanded' ? expandedWidth : kSidebarCollapsedWidth;

  return (
    <View style={{ flex: 1, flexDirection: 'row' }}>
      <View style={{ width: pinnedWidth }}>
        <AppSidebar
          collapsed={state !== 'expanded'}
          nextStateHint={state === 'expanded' ? 'collapse' : 'expand'}
          onToggleCollapsed={cycle}
        />
      </View>
      <LayoutProviderView style={{ flex: 1, overflow: 'hidden' }}>
        {drawer}
      </LayoutProviderView>

      {state === 'floating' && (
        <>
          {/* Backdrop closes the floating panel. Sits between the
              pinned rail and the floating panel so the rail stays
              clickable underneath. */}
          <Pressable
            onPress={closeFloating}
            style={{
              position: 'absolute',
              left: kSidebarCollapsedWidth,
              top: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.25)',
            }}
          />
          <View
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: kSidebarFloatingWidth,
              shadowColor: '#000',
              shadowOffset: { width: 4, height: 0 },
              shadowOpacity: 0.25,
              shadowRadius: 12,
              elevation: 8,
            }}
          >
            <AppSidebar
              collapsed={false}
              nextStateHint="expand"
              onToggleCollapsed={cycle}
              onItemNavigated={closeFloating}
            />
          </View>
        </>
      )}
    </View>
  );
});

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  return useMemo(
    () =>
      StyleSheet.create({
        shadow: {
          shadowColor: '#7f5df0',
          shadowOffset: {
            width: 0,
            height: 1,
          },
          shadowOpacity: 0.25,
          shadowRadius: rpx(5),
          elevation: 5,
        },
      }),
    [rpx],
  );
};
